// Вкладка «Рефералы»: кто кого пригласил, что начислено. Подписи, столбцы и настройки приходят с сервера готовыми
// (GET /api/admin/referrals), код здесь только показывает их и отправляет введённое.
import { useState } from 'react';
import type { DialogProps } from '../../core/registry';
import { ctx } from '../../core/ctx';
import { Btn, Field, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { askConfirm } from '../../ui/prompts';
import { ViewSummary, ViewTableBlock } from '../auth/referrals';
import { adminApi, type ARefLabels } from './adminApi';
import { act, LoadError, useLoad } from './common';

export function SettleDialog({ props, close }: DialogProps<boolean>) {
  const labels = props.labels as ARefLabels;
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      await adminApi.settle(props.uid as number, { amount, note });
      ctx.toast(labels.done, 'ok');
      close(true);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };
  return (
    <Modal title={`${labels.settle}: ${props.login}`} onClose={() => close()} width={460} testid="ref-settle-dialog" footer={<>
      <Btn label={labels.cancel} onClick={() => close()} />
      <Btn label={labels.save} primary onClick={() => void save()} disabled={busy || !amount.trim()} testid="ref-settle-save" />
    </>}>
      <div className="form-grid">
        <Field label={labels.amount} wide><input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" autoFocus data-testid="ref-settle-amount" /></Field>
        <Field label={labels.note} wide><input value={note} onChange={(e) => setNote(e.target.value)} placeholder={labels.notePlaceholder} maxLength={200} data-testid="ref-settle-note" /></Field>
      </div>
      {err && <div className="adm-error" role="alert" data-testid="ref-settle-error"><Icon name="error" size={16} /><span>{err}</span></div>}
    </Modal>
  );
}

export function RefDetailDialog({ props, close }: DialogProps<boolean>) {
  const labels = props.labels as ARefLabels;
  const uid = props.uid as number;
  const { data, error, reload } = useLoad(() => adminApi.referralDetail(uid), [uid]);
  return (
    <Modal title={`${labels.details}: ${props.login}`} onClose={() => close(true)} width={760} testid="ref-detail-dialog" footer={<Btn label={labels.close} onClick={() => close(true)} />}>
      <LoadError error={error} reload={reload} />
      {data && <>
        <ViewSummary items={data.summary} />
        {data.tables.map((t, i) => <ViewTableBlock key={i} table={t} action={t.removable ? (r) => (
          <Btn icon="trash" title={labels.remove} danger testid="ref-entry-remove" onClick={async () => {
            if (await askConfirm({ title: labels.remove, text: labels.removeAsk, ok: labels.removeOk, danger: true })) void act(() => adminApi.unsettle(r._id), labels.removed, reload);
          }} />) : undefined} />)}
      </>}
    </Modal>
  );
}

export function ReferralsTab() {
  const { data, error, reload } = useLoad(() => adminApi.referrals(), []);
  const [edit, setEdit] = useState<Record<string, string>>({});
  const saveField = (key: string) => {
    const v = edit[key];
    if (v === undefined) return;
    void act(() => adminApi.saveSettings({ [key]: Number(v) } as any), undefined, async () => { setEdit((e) => { const n = { ...e }; delete n[key]; return n; }); await reload(); });
  };
  const labels = data?.labels;
  return (
    <section className="adm-tab" data-testid="admin-referrals">
      <LoadError error={error} reload={reload} />
      {data && labels && <>
        <p className="adm-note" data-testid="ref-intro"><Icon name="info" size={14} /> {labels.intro}</p>
        <div className="adm-card" data-testid="ref-fields">
          {data.fields.map((f) => (
            <label key={f.key} className="adm-set-row">
              <span className="adm-set-label">{f.label}<span className="adm-set-hint">{f.hint}</span></span>
              <span className="adm-num"><input type="number" min={0} max={100} value={edit[f.key] ?? f.value} data-testid={'ref-field-' + f.key}
                onChange={(e) => setEdit((x) => ({ ...x, [f.key]: e.target.value }))} onBlur={() => saveField(f.key)} /><span className="adm-unit">{f.unit}</span></span>
            </label>
          ))}
        </div>
        <ViewSummary items={data.summary} />
        <ViewTableBlock table={data.table} action={(r) => <>
          <Btn icon="file" title={labels.details} testid="ref-details" onClick={async () => { await ctx.openDialog('admin.refDetail', { uid: r._id, login: r.login, labels }); void reload(); }} />
          {r._settle && <Btn icon="ok" label={labels.settle} testid="ref-settle" onClick={async () => { if (await ctx.openDialog('admin.refSettle', { uid: r._id, login: r.login, labels })) void reload(); }} />}
        </>} />
      </>}
    </section>
  );
}
